import type { JSX, ReactNode, SVGProps } from 'react';

/**
 * Значки плавающего «+» — те же lucide-значки, что в виджете обращений CRM
 * (frontend/src/components/feedback/FeedbackWidget.tsx): 24×24, линия 2 px,
 * цвет — currentColor. lucide-react в wiki не подключён (импорт между пакетами
 * запрещён, новая зависимость — лишний вес), поэтому пути скопированы
 * (lucide-react 1.14.0, лицензия ISC).
 */
function Svg({ size = 18, children, ...rest }: { size?: number; children: ReactNode } & Omit<SVGProps<SVGSVGElement>, 'children'>): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

type IconProps = { size?: number };

export const IconPlus = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </Svg>
);

export const IconX = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Svg>
);

export const IconCircleHelp = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </Svg>
);

export const IconBug = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M12 20v-9" />
    <path d="M14 7a4 4 0 0 1 4 4v3a6 6 0 0 1-12 0v-3a4 4 0 0 1 4-4z" />
    <path d="M14.12 3.88 16 2" />
    <path d="M21 21a4 4 0 0 0-3.81-4" />
    <path d="M21 5a4 4 0 0 1-3.55 3.97" />
    <path d="M22 13h-4" />
    <path d="M3 21a4 4 0 0 1 3.81-4" />
    <path d="M3 5a4 4 0 0 0 3.55 3.97" />
    <path d="M6 13H2" />
    <path d="m8 2 1.88 1.88" />
    <path d="M9 7.13V6a3 3 0 1 1 6 0v1.13" />
  </Svg>
);

export const IconLightbulb = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </Svg>
);

export const IconClipboardList = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M12 11h4" />
    <path d="M12 16h4" />
    <path d="M8 11h.01" />
    <path d="M8 16h.01" />
  </Svg>
);

export const IconArrowLeft = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </Svg>
);

export const IconMaximize = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M15 3h6v6" />
    <path d="m21 3-7 7" />
    <path d="m3 21 7-7" />
    <path d="M9 21H3v-6" />
  </Svg>
);

export const IconMinimize = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="m14 10 7-7" />
    <path d="M20 10h-6V4" />
    <path d="m3 21 7-7" />
    <path d="M4 14h6v6" />
  </Svg>
);

export const IconPaperclip = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" />
  </Svg>
);

export const IconSend = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
    <path d="m21.854 2.147-10.94 10.939" />
  </Svg>
);

export const IconBookOpen = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M12 7v14" />
    <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
  </Svg>
);

export const IconExternalLink = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </Svg>
);

export const IconCamera = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z" />
    <circle cx="12" cy="13" r="3" />
  </Svg>
);

export const IconUndo = ({ size }: IconProps): JSX.Element => (
  <Svg size={size}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11" />
  </Svg>
);
